// client/src/components/PrivacyPolicy.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import AccountDropdown from './AccountDropdown';
import logo from '../assets/logo128x128.png';
import '../Homepage.css';

export default function PrivacyPolicy({ user, setUser }) {
  const navigate = useNavigate();

  return (
    <div className="homepage" style={{ minHeight: '100vh', background: '#111', color: '#eee' }}>
      <header className="homepage-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <img
          src={logo}
          alt="Point Atlas Logo"
          style={{ width: 40, height: 40, objectFit: 'contain', cursor: 'pointer' }}
          onClick={() => navigate('/')}
          title="Go to Home"
        />
        <h1 className="brand" style={{ margin: 0 }}>
          Point Atlas – Privacy Policy & Terms
        </h1>
        {user && <AccountDropdown user={user} setUser={setUser} />}
      </header>

      <div
        style={{
          padding: '40px 20px',
          maxWidth: '900px',
          margin: '0 auto',
          lineHeight: 1.6,
        }}
      >
        <h2 style={{ marginBottom: 12 }}>Privacy Policy</h2>

        <p>
          Point Atlas is built around openness, collaboration and trust. We collect as little information as possible,
          and we do not sell your personal data.
        </p>

        <p>
          Because Point Atlas uses <strong>Google Login</strong>, we do not store or handle passwords of any kind. When
          you sign in with Google, Google sends us your email address, your Google ID, your public profile name and
          (optionally) your profile image. We do not request or access anything else from your Google account.
        </p>

        <p>
          Your email is used for identifying your account and for optional service notifications (for example, replies
          to your comments or updates to maps you own). We do not sell your email address, and we do not share it with
          third parties for their own marketing.
        </p>

        <p>
          We store the content you intentionally create: maps you upload, POIs you add, comments you post, votes and
          favourites, and related account activity required to operate the service. Some features (such as personal
          pins, found POI tracking and certain UI preferences) may be stored locally on your device and never leave your
          browser unless you explicitly choose to save them to the server.
        </p>

        <h3 style={{ marginTop: 24 }}>Advertising</h3>

        <p>
          Point Atlas may display advertisements on map pages <strong>only where the map owner has chosen to enable
          advertising</strong> for that map. Ads are optional and are not shown on maps unless the owner explicitly opts
          in via the available monetisation settings.
        </p>

        <p>
          Where a map owner has enabled advertising, ads are served via third-party advertising providers (for example,
          NitroPay and its demand partners). Ads may not appear at all times and may vary by page, region, device, and
          available demand. Ad delivery, fill rates, and CPM are not guaranteed.
        </p>

        <p>
          When ads are shown, the advertising provider and its partners may automatically collect certain technical
          information to deliver, measure, and protect ads, such as: IP address, device and browser details, approximate
          location (derived from IP), page URL/referrer, timestamps, and ad interaction/diagnostic events. They may also
          set or read cookies or similar identifiers (for example, local storage identifiers) on your device for
          frequency capping, fraud prevention, reporting, and (where applicable) ad personalisation.
        </p>

        <p>
          You can control cookies in your browser settings, and you can use content blockers or ad blockers if you
          prefer. Note that blocking cookies or scripts may reduce functionality or prevent ads from rendering correctly,
          but core site features should remain available.
        </p>

        <h3 style={{ marginTop: 24 }}>Tips and tip bars (optional)</h3>

        <p>
          Some maps may display a <strong>tip bar</strong> (for example, a link or button allowing you to support the map
          owner directly) instead of advertisements or alongside them. Tip bars are optional and are enabled or disabled
          by the map owner.
        </p>

        <p>
          Where a tip bar is used, tips are processed by third-party payment providers (for example, PayPal, Stripe, Ko-fi
          or similar) and are subject to that provider’s own terms, fees, and privacy practices. Point Atlas does not
          process card details and does not store full payment information.
        </p>

        <p>
          <strong>Point Atlas takes 0% commission from tips.</strong> Any fees charged by the payment provider (and any
          applicable taxes) are handled by that provider and/or the map owner.
        </p>

        <h3 style={{ marginTop: 24 }}>Cookies and local storage</h3>

        <p>
          We use cookies and similar storage for essential login/session functionality and to remember basic preferences.
          If advertising is enabled on a map page by its owner, third-party advertising providers may also use cookies or
          similar identifiers as described above. If a tip bar links to an external payment provider, that provider may
          use cookies or similar technologies on their own pages.
        </p>

        <h3 style={{ marginTop: 24 }}>Map owner earnings and revenue share</h3>

        <p>
          Point Atlas may provide map owners with an option to monetise eligible public maps through advertising. Where a
          map owner has enabled advertising and the map is approved for monetisation, the map owner may receive a
          revenue share of <strong>60%</strong> of the net advertising revenue attributable to that map’s pages, subject
          to all of the following:
        </p>

        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>
            <strong>Opt-in by the map owner:</strong> advertising is entirely optional and must be explicitly enabled by
            the map owner for each map.
          </li>
          <li>
            <strong>Approval and eligibility:</strong> monetisation is subject to approval by Point Atlas and/or our
            advertising partners. Not all maps, accounts, pages, regions, or traffic sources will be eligible.
          </li>
          <li>
            <strong>Ad delivery is not guaranteed:</strong> ads may not fill on every view, and fill rates and CPM can
            vary materially over time.
          </li>
          <li>
            <strong>Measurement basis:</strong> for performance reporting, we may track only page views/impressions and
            related ad-delivery diagnostics. We do not guarantee that internal view counts will match the advertising
            provider’s reporting.
          </li>
          <li>
            <strong>Estimates only:</strong> any earnings figures shown in dashboards or reports are estimates only and
            may change as impression counts, fill, CPM, invalid traffic filtering, chargebacks, adjustments, or
            provider reconciliations occur.
          </li>
          <li>
            <strong>Anti-fraud and policy enforcement:</strong> we may withhold, adjust, or reverse earnings where we
            reasonably believe there is invalid traffic, policy breaches, or other misuse.
          </li>
        </ul>

        <p>
          Final earnings and any payouts (if offered) are determined by Point Atlas based on reconciled reporting from
          our advertising partners and any applicable adjustments. Additional terms may apply, including payout
          thresholds, timing, supported payout methods, and required tax or identity information.
        </p>

        <p>
          If revenue sharing is enabled for your account, payouts are processed via a third-party payment provider. The
          email address associated with your Point Atlas account may be used to invite you to connect a payout account
          with that provider. Point Atlas does not store your bank account or card details.
        </p>

        <p>
          Tips (where enabled via a tip bar) are separate from advertising revenue share. <strong>Point Atlas takes 0%
          commission from tips</strong>.
        </p>

        <h3 style={{ marginTop: 24 }}>Data sharing</h3>

        <p>
          We share data with service providers only to operate Point Atlas (for example, authentication, hosting, and ad
          delivery). We do not authorise third parties to use your personal data for their own direct marketing.
        </p>

        <h3 style={{ marginTop: 24 }}>Data retention and deletion</h3>

        <p>
          If you wish to delete your account, export your data, or remove any of your content, contact us at{' '}
          <strong>contact@point-atlas.io</strong>.
        </p>

        <h2 style={{ marginTop: 40, marginBottom: 12 }}>Terms &amp; Conditions</h2>

        <p style={{ fontStyle: 'italic' }}>Last updated: 22 January 2026</p>

        <p>
          Welcome to Point Atlas (the “Service”). By accessing or using Point Atlas, you agree to these Terms &amp;
          Conditions. If you do not agree, please do not use the Service.
        </p>

        <h3 style={{ marginTop: 24 }}>1. About Point Atlas</h3>
        <p>Point Atlas is a platform that allows users to:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Create interactive maps for games, worlds, D&amp;D campaigns, and other fictional or real locations</li>
          <li>Upload or import images, overlays, icons, JSON map data and POIs</li>
          <li>Browse maps created by others</li>
          <li>Leave comments, feedback, and personal notes</li>
          <li>Favourite maps and vote on them</li>
          <li>Store personal pins and client-side configuration in their browser</li>
          <li>Share maps publicly or keep them private</li>
          <li>Optionally enable advertising or a tip bar on their maps</li>
        </ul>
        <p>Point Atlas is operated by Point Atlas LTD.</p>

        <h3 style={{ marginTop: 24 }}>2. Eligibility</h3>
        <p>You must:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Be at least 13 years old (or older if required by your region)</li>
          <li>Have legal permission to use the internet and submit user-generated content</li>
        </ul>
        <p>Parents or guardians are responsible for any use by minors.</p>

        <h3 style={{ marginTop: 24 }}>3. User Accounts</h3>
        <p>
          You may need an account to create, edit, or save maps. You agree to provide accurate information, keep your
          login secure, and be responsible for all activity under your account.
        </p>
        <p>
          We reserve the right to suspend or delete accounts that violate these terms or are used in a harmful or abusive
          way.
        </p>

        <h3 style={{ marginTop: 24 }}>4. Definitions</h3>
        <p>
          For the purposes of these Terms:
        </p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>
            <strong>“Map owner”</strong> means the registered user account that created a map or to whom ownership of a
            map has been formally transferred using Point Atlas’s ownership or sharing features. The map owner controls
            the map’s visibility, editing permissions, and whether advertising, tip bars, or monetisation is enabled for
            that map.
          </li>
        </ul>

        <h3 style={{ marginTop: 24 }}>5. User-Generated Content (UGC)</h3>
        <p>UGC includes, but is not limited to:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>POIs, text descriptions, images, icons, overlays</li>
          <li>Map data, coordinates, metadata, imported JSON</li>
          <li>Comments, feedback, private notes and personal pins</li>
          <li>Any content uploaded or created on the platform</li>
        </ul>
        <p>By submitting UGC, you confirm that:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>You own it or have permission to use it</li>
          <li>It does not infringe any copyright, trademark, or third-party rights</li>
          <li>It does not contain illegal, hateful, or harmful material</li>
          <li>It follows applicable laws</li>
        </ul>
        <p>
          <strong>Your rights:</strong> You retain ownership of your UGC.
        </p>
        <p>
          <strong>Our rights:</strong> You grant Point Atlas a non-exclusive, worldwide, royalty-free licence to:
        </p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Store and display your content</li>
          <li>Modify it when needed for technical reasons (resizing, formatting, converting)</li>
          <li>Distribute it to users viewing your maps</li>
          <li>Use it to operate and improve the service</li>
        </ul>
        <p>We do not claim exclusive ownership over your content.</p>
        <p>You may delete your maps or request deletion of associated data.</p>

        <h3 style={{ marginTop: 24 }}>6. Imported Maps &amp; Third-Party Material</h3>
        <p>
          If you upload or import maps or assets from a game, film, book, campaign, or real-world location, you are
          responsible for ensuring you have rights to use and share that material.
        </p>
        <p>
          Point Atlas cannot verify copyrights of imported assets and does not accept liability for misused third-party
          materials. We may remove content if we receive legitimate copyright complaints.
        </p>

        <h3 style={{ marginTop: 24 }}>7. Private Data, Personal Pins &amp; Local Storage</h3>
        <p>Some data is stored locally in your browser, including:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Personal pins</li>
          <li>Visibility filters</li>
          <li>Sidebar expansion state</li>
          <li>Map drawer state</li>
          <li>Theme and styling preferences</li>
        </ul>
        <p>This data is never sent to the server unless explicitly saved by you.</p>

        <h3 style={{ marginTop: 24 }}>8. Map Ownership &amp; Visibility</h3>
        <p>Map creators may choose:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Public maps</li>
          <li>Private or unlisted maps</li>
          <li>Community or region-linked maps</li>
        </ul>
        <p>Creators are responsible for:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Ensuring their content follows these terms</li>
          <li>Moderating or updating their maps</li>
          <li>Responding to feedback or deletion requests</li>
        </ul>
        <p>We may hide or remove maps that violate rules, copyright, or community standards at our discretion.</p>

        <h3 style={{ marginTop: 24 }}>9. Prohibited Content</h3>
        <p>You may not upload, display, link, or distribute:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Copyrighted material you do not own or have rights to use</li>
          <li>Pornographic, hateful, violent, or illegal content</li>
          <li>Malware, tracking scripts, or harmful code</li>
          <li>Personal data belonging to others</li>
          <li>Real-world sensitive locations where disclosure may cause harm</li>
        </ul>
        <p>We reserve the right to remove any content at our discretion.</p>

        <h3 style={{ marginTop: 24 }}>10. Comments, Voting &amp; Community Behaviour</h3>
        <p>By participating in discussions, you agree not to:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Harass or abuse other users</li>
          <li>Post spam</li>
          <li>Post misleading or dangerous information</li>
          <li>Engage in vote manipulation or brigading</li>
        </ul>
        <p>We may remove comments or restrict access for violations.</p>

        <h3 style={{ marginTop: 24 }}>11. Map Accuracy Disclaimer</h3>
        <p>Maps on Point Atlas can be:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Player-created</li>
          <li>Fictional</li>
          <li>Incomplete, outdated, or user-contributed</li>
        </ul>
        <p>We do not guarantee accuracy or safety of any information on the platform. Use the maps at your own risk.</p>

        <h3 style={{ marginTop: 24 }}>12. Intellectual Property</h3>
        <p>Point Atlas owns:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>The website and platform</li>
          <li>The underlying code</li>
          <li>The UI, branding, and infrastructure</li>
          <li>Our tools, including POI systems, pathing, overlay tools, region switching, and map APIs</li>
        </ul>
        <p>
          You may not copy, reverse engineer, or redistribute the platform. You may screenshot or share maps as part of
          normal use.
        </p>

        <h3 style={{ marginTop: 24 }}>13. Service Availability</h3>
        <p>
          We may change, suspend, or discontinue the Service at any time. We are not liable for loss of data, downtime,
          feature changes, or removal of content that violates policy.
        </p>
        <p>Back up your maps regularly using Export/JSON tools.</p>

        <h3 style={{ marginTop: 24 }}>14. Limitation of Liability</h3>
        <p>
          To the maximum extent allowed by law, Point Atlas is provided “as is” and “as available”. We give no
          guarantees or warranties.
        </p>
        <p>
          We are not responsible for losses arising from user-generated content, bugs, downtime or data loss, incorrect
          map information, third-party copyright claims, or user misconduct. Your use of the platform is entirely at
          your own risk.
        </p>

        <h3 style={{ marginTop: 24 }}>15. Termination</h3>
        <p>
          We may suspend or terminate accounts that break these terms, upload harmful or illegal content, abuse the
          service or its users, or engage in fraud or copyright violations.
        </p>
        <p>Upon termination, we may delete or hide maps depending on severity.</p>

        <h3 style={{ marginTop: 24 }}>16. Changes to These Terms</h3>
        <p>
          We may update these Terms at any time. Significant changes may be announced on-site or via email. Continued
          use of the Service means you accept the updated terms.
        </p>

        <h3 style={{ marginTop: 24 }}>17. Contact</h3>
        <p>
          For questions, copyright concerns, or deletion requests, contact: <strong>support@point-atlas.io</strong>.
        </p>
      </div>
    </div>
  );
}
